import React, { useState, useCallback } from 'react';
import { Popover, Input, Button, message } from 'antd';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../../context/complexVersionContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../../context/complexDocumentContext';
import { createVersionTag } from '../../../utils/services/complexVersionsAPI';
import { TagsPopup } from '../VersionSettings/styles';
import { VERSION_APPROVAL_PROCESS } from '../translationValues';
import { COMPLEX_DOCUMENT_VERSION } from '../../../utils/complexDocumentConstants';

export const CreateTags = props => {
  const {
    t,
    handleSendForTesting,
    handlePublishProcess,
    updateVersionStore,
    showTestingTagsPopup,
    setShowTestingTagsPopup,
    showPublishingTagsPopup,
    setShowPublishingTagsPopup,
  } = props;

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, `versionSettings`);

  const isEmailSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE],
    [],
  );
  const [isEmailTemplate] = useDocumentContext(isEmailSelector, `versionSettings`);

  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, `versionSettings`);

  const versionsSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.TAGS_LIST], []);
  const [versions] = useVersionContext(versionsSelector, `versionSettings`);

  const [tagName, setTagName] = useState('');
  const [isVisible, setIsVisible] = useState(showTestingTagsPopup || showPublishingTagsPopup);
  const [isTagsCreating, setIsTagsCreating] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleVisibleChange = visibilityState => {
    if (showTestingTagsPopup) {
      setShowTestingTagsPopup(false);
    } else if (showPublishingTagsPopup) {
      setShowPublishingTagsPopup(false);
    }
    setIsVisible(visibilityState);
  };

  const handleSkip = () => {
    if (showTestingTagsPopup) {
      handleSendForTesting();
      setShowTestingTagsPopup(false);
    } else if (showPublishingTagsPopup) {
      handlePublishProcess();
      setShowPublishingTagsPopup(false);
    }
    setIsVisible(false);
  };
  const createTag = async () => {
    setIsError(false);
    if (!tagName.trim().length) {
      message.error(
        t(
          VERSION_APPROVAL_PROCESS.TAG_NAME_EMPTY_ERROR.key,
          VERSION_APPROVAL_PROCESS.TAG_NAME_EMPTY_ERROR.value,
        ),
      );
      setIsError(true);
      return;
    }
    setIsTagsCreating(true);
    try {
      const environment = showPublishingTagsPopup ? COMPLEX_DOCUMENT_VERSION.LIVE : showTestingTagsPopup ? COMPLEX_DOCUMENT_VERSION.SANDBOX : '';
      const payload = {
        env: environment,
        tagName: tagName,
        channel: isEmailTemplate ? 'email' : 'print',
        designType: currentDesign,
        designId: documentId,
      };
      const response = await createVersionTag(payload);
      if (response?.statusCode === 5700) {
        updateVersionStore({
          [VERSION_CONTEXT_KEYS.TAGS_LIST]: [response?.data, ...versions],
        });
        if (showTestingTagsPopup) {
          setShowTestingTagsPopup(false);
          handleSendForTesting();
        } else if (showPublishingTagsPopup) {
          setShowPublishingTagsPopup(false);
          handlePublishProcess();
        }
      } else if (
        response?.statusCode === 5701 &&
        response?.errors?.some(err => err.code === 5700)
      ) {
        isError(true);
        message.error(
          t(
            VERSION_APPROVAL_PROCESS.TAG_EXISTS_ERROR.key,
            VERSION_APPROVAL_PROCESS.TAG_EXISTS_ERROR.value,
          ),
        );
      } else {
        message.error(t(
          VERSION_APPROVAL_PROCESS.TAG_CREATION_FAILURE.key,
          VERSION_APPROVAL_PROCESS.TAG_CREATION_FAILURE.value,
        ));
      }
    } catch (e) {
      message.error(t(
        VERSION_APPROVAL_PROCESS.TAG_CREATION_FAILURE.key,
        VERSION_APPROVAL_PROCESS.TAG_CREATION_FAILURE.value,
      ));
    } finally {
      setIsTagsCreating(false);
    }
  };

  const customContent = () => {
    return (
      <>
        <TagsPopup>
          <div style={{ width: 250 }}>
            <p style={{ marginBottom: 8 }}>
              {showPublishingTagsPopup
                ? t(
                  VERSION_APPROVAL_PROCESS.CREATE_TAG_HEADER_FOR_PUBLISH.key,
                  VERSION_APPROVAL_PROCESS.CREATE_TAG_HEADER_FOR_PUBLISH.value,
                )
                : t(
                  VERSION_APPROVAL_PROCESS.CREATE_TAG_HEADER.key,
                  VERSION_APPROVAL_PROCESS.CREATE_TAG_HEADER.value,
                )}
            </p>
            <label style={{ fontWeight: 600 }}>
              {t(
                VERSION_APPROVAL_PROCESS.TAG_NAME_LABEL.key,
                VERSION_APPROVAL_PROCESS.TAG_NAME_LABEL.value,
              )}
            </label>
            <Input
              className="tag-input"
              style={{
                borderColor: isError ? 'red' : undefined
              }}
              value={tagName}
              onChange={e => setTagName(e.target.value)}
              onPressEnter={createTag}
              autoFocus
              placeholder={t(
                VERSION_APPROVAL_PROCESS.PLACEHOLDER_TAG_NAME.key,
                VERSION_APPROVAL_PROCESS.PLACEHOLDER_TAG_NAME.value,
              )}
            />
            <div className="footer-btn">
              <Button
                loading={isTagsCreating}
                type="primary"
                style={{ background: 'black' }}
                onClick={() => {
                  createTag();
                }}
              >
                {t(
                  VERSION_APPROVAL_PROCESS.CREATE_TAG.key,
                  VERSION_APPROVAL_PROCESS.CREATE_TAG.value,
                )}
              </Button>
              <Button
                type="text"
                onClick={() => {
                  handleSkip();
                }}
              >
                {t(
                  VERSION_APPROVAL_PROCESS.SKIP_AND_PROCEED.key,
                  VERSION_APPROVAL_PROCESS.SKIP_AND_PROCEED.value,
                )}
              </Button>
            </div>
          </div>
        </TagsPopup>
      </>
    );
  };

  return (
    <TagsPopup>
      <Popover getPopupContainer={trigger => trigger.parentNode} style={{ width: '75%', borderRadius: '16px', outline: '1px solid grey', padding: '10px' }} visible={isVisible} trigger="click" placement="bottom" content={customContent()} onVisibleChange={handleVisibleChange} />
    </TagsPopup>
  );
};
